<template>
  <div v-if="!isAuthenticated" class="text-justify">
    <md-card style="min-height: 451px;">
      <md-card-header class="card-divider">
        <h3><b>Anmeldung</b></h3>
        <p v-if="$route.query.redirect">
          Sie müssen sich zuerst anmelden.
        </p>
      </md-card-header>
      <md-card-content style="padding-top: 1rem">
        <div v-if="webAppConfig.soseLoginDisabled && magic !== webAppConfig.soseLoginMagic">
          <p style="font-size: larger">
            Lieber SoSE-Nutzer,
            <br><br>
            derzeit steht das System aufgrund von aktuellen <b style="color: seagreen">Wartungsarbeiten</b> nicht zur
            Verfügung.
            Sobald diese beendet sind, können Sie sich hier wieder anmelden.
            <br><br>
            Vielen Dank für Ihr Verständnis.
            <br>
            ai42 Team
          </p>
          <br>
          <md-progress-bar md-mode="buffer"/>
          <br>
        </div>
        <div v-else>
          <form v-if="!twoFactorAuthRequired" novalidate @submit.prevent="validateUser">
            <md-field :class="getValidationClass('email')">
              <label for="email">Email</label>
              <md-input type="email" name="email" id="email" autocomplete="email" v-model="form.email"
                        :disabled="sending"/>
              <span class="md-error" v-if="!$v.form.email.required">Email-Adresse ist notwendig</span>
              <span class="md-error" v-else-if="!$v.form.email.email">Ungültige Email-Adresse</span>
            </md-field>
            <md-field :class="getValidationClass('password')">
              <label for="password">Passwort</label>
              <md-input name="password" id="password" autocomplete="passwort" v-model="form.password"
                        :disabled="sending" type="password"/>
              <span class="md-error" v-if="!$v.form.password.required">Passwort ist notwendig</span>
              <span class="md-error"
                    v-if="!$v.form.password.length && $v.form.password.required">Ungültiges Passwort</span>
            </md-field>
            <div v-if="sending">
              <br><br>
              <vue-simple-spinner></vue-simple-spinner>
            </div>
            <div v-else>
              <router-link to="/Passwort/Vergessen">
                <div class="text-center"><a>Passwort vergessen</a></div>
                <md-tooltip md-direction="bottom" style="font-size: medium;">Neues Passwort erstellen</md-tooltip>
              </router-link>
              <br>
              <SocialMediaLinks/>
              <br>
              <button class="button expanded" type="submit"><i class="fi-torso"></i>&nbsp;<i class="fi-key"></i>&nbsp;&nbsp;&nbsp;Anmelden
              </button>
            </div>
          </form>
          <div v-if="twoFactorAuthRequired">
            <h6>Zwei-Faktor-Authentifizierung (2FA)</h6>
            <br>

            <label v-if="requestRecoveryCode" class="small-label">
              Sie haben einen <b>Wiederherstellungscode</b> angefordert.
              Dieser wurde an Ihre hinterlegte <b>E-Mail-Adresse</b> gesendet. Geben Sie diesen Code bitte in das Feld <b>Wiederherstellungscode</b> ein.
            </label>
            <label v-else class="small-label">
              Zur Authentifizierung wird ein <b>One Time Password</b> (OTP) benötigt.
              Starten Sie ihre <b>Authenticator</b> App und geben den angezeigten <b>Bestätigungscode</b> in das Feld <b>One Time Password</b> (OTP) ein.
            </label>
            <br>

            <md-field :class="get2FAValidationClass()">
              <label v-if="requestRecoveryCode"><b>Wiederherstellungscode</b></label>
              <label v-else><b>One Time Password</b> (OTP)</label>
              <md-input name="one-time-password" id="one-time-password-id" autocomplete="off" v-model="confirmation2faOTP"
                        :disabled="sending"/>
              <span class="md-error" v-if="validateConfirmation2faOTP.required">One Time Password ist notwendig</span>
              <span class="md-error" v-else-if="validateConfirmation2faOTP.minLength">One Time Password muss mindestens 6 Zeichen sein</span>
            </md-field>
            <br>

            <div v-if="sending" style="margin-bottom: 4rem">
              <md-progress-bar md-mode="indeterminate"/>
            </div>

            <div class="grid-x">
              <div class="cell large-4 medium-4 small-4 text-left">
                <button v-if="!sending" class="button secondary-transparent" type="submit" @click="twoFactorAuthRequired = false; confirmation2faOTP = '';requestRecoveryCode = false; twoFactorRecoveryCode = '';">
                  <i class="fi-arrow-left"></i>&nbsp;&nbsp;&nbsp;Zurück
                </button>
              </div>
              <div class="cell large-8 medium-8 small-8 text-right">
                <button v-if="!sending && !requestRecoveryCode" class="button success" type="submit" @click="loginWith2fa">
                  <i class="fi-shield"></i>&nbsp;&nbsp;&nbsp;2FA Bestätigen&nbsp;&nbsp;&nbsp;<i class="fi-arrow-right"></i>
                </button>
                <button v-if="!sending && requestRecoveryCode" class="button success-transparent" type="submit" @click="recover2fa">
                  <i class="fi-shield"></i>&nbsp;&nbsp;&nbsp;2FA Wiederherstellen&nbsp;&nbsp;&nbsp;<i class="fi-arrow-right"></i>
                </button>
              </div>
            </div>

            <button v-if="!sending && OTPAuthenticationFailed && !requestRecoveryCode" class="button expanded success-transparent" type="submit" @click="onRequestRecoveryCode">
              <i class="fi-wrench"></i>&nbsp;&nbsp;&nbsp;Wiederherstellungscode anfordern
            </button>

          </div>
        </div>
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import Spinner from 'vue-simple-spinner'
import LoginService from '../../services/LoginService'
import UserService from '../../services/UserService'
import InfoService from '../../services/InfoService'
import HttpErrorHandler from '../../services/HttpErrorHandler'
import HttpInterceptor from '../../services/HttpInterceptor'

import {validationMixin} from 'vuelidate'
import {
  required,
  email,
  minLength
} from 'vuelidate/lib/validators'
import OrganizationService from "../../services/OrganizationService";
import Vue from "vue";
import Address from "../../entities/Address";
import TestAccountData from "../../entities/TestAccountData";
import SocialMediaLinks from "@/components/views/social/SocialMediaLinks";
import OrganizationInfoService from "../../services/OrganizationInfoService";
import HttpUrlService from "../../services/HttpUrlService";
import jQuery from "jquery";

export default {
  name: 'Login',
  mixins: [validationMixin],

  components: {
    SocialMediaLinks,
    'vue-simple-spinner': Spinner
  },

  mounted() {
    this.magic = this.$route.params['magic'];
    this.cleanLocalStorage();
  },

  beforeDestroy() {
    this.init2faInputSyncHandler(false);
  },

  data: () => ({
    form: {
      email: '',
      password: '',
    },
    sending: false,
    magic: '',
    twoFactorAuthRequired: false,

    demand: {
      account: {
        accountOwner: '',
        bankname: '',
        bic: '',
        dateOfMandateGranting: Vue.moment(),
        iban: '',
        mandateReference: '',
        numberOfSuccessfulSEPATransactions: 0,
        valid: true,
        mandateType: 'ONE_OFF'
      },

      advertisingToken: null,
      agbConfirmed: false,
      ai42Contract: null,
      contractConfirmed: true,
      dsgvoConfirmed: false,
      email: '',
      keepDataOfTestphase: false,
      modules: [],
      numChildren: 5,
      organizationAddress: Address.address,
      organizationName: '',
      organizationType: '',
      partnerMember: true,
      password: '',
      paymentInterval: 'MONTHLY',
      testAccountDemand: false,
      testAccountData: TestAccountData.testAccountData,
      useSepaForPayment: true,
    },

    confirmation2faOTP: '',
    requestRecoveryCode: false,
    twoFactorRecoveryCode: '',
    OTPAuthenticationFailed: false,

    validateConfirmation2faOTP: {
      required: false,
      minLength: false,
      invalid: false,
      dirty: false,
    }
  }),

  methods: {

    init2faInputSyncHandler(init) {
      if (init) {
        setTimeout(() => {
          jQuery('#one-time-password-id').bind('keyup', () => {
            this.confirmation2faOTP = jQuery('#one-time-password-id').val();
            this.validate2fa();
          });
        }, 500);
      } else {
        jQuery('#one-time-password-id').unbind('keyup');
      }
    },

    getValidationClass(fieldName) {
      const field = this.$v.form[fieldName];
      if (field) {
        return {
          'md-invalid': field.$invalid && field.$dirty
        }
      }
    },

    validateUser() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.login();
      }
    },

    validate2fa() {
      this.validateConfirmation2faOTP.required = false;
      this.validateConfirmation2faOTP.invalid = false;
      this.validateConfirmation2faOTP.minLength = false;

      if (this.confirmation2faOTP.length <= 0) {
        this.validateConfirmation2faOTP.required = true;
        this.validateConfirmation2faOTP.invalid = true;
      }

      if (this.confirmation2faOTP.length > 0 && this.confirmation2faOTP.length < 6) {
        this.validateConfirmation2faOTP.minLength = true;
        this.validateConfirmation2faOTP.invalid = true;
      }
    },

    recover2fa() {
      this.validateConfirmation2faOTP.dirty = true;
      this.validate2fa();

      if (this.validateConfirmation2faOTP.required === false && this.validateConfirmation2faOTP.invalid === false && this.validateConfirmation2faOTP.minLength === false) {
        this.twoFactorRecoveryCode = this.confirmation2faOTP;
        this.confirmation2faOTP = '';
        this.requestRecoveryCode = false;
        this.login();
      }
    },

    loginWith2fa() {
      this.validateConfirmation2faOTP.dirty = true;
      this.validate2fa();

      if (this.validateConfirmation2faOTP.required === false && this.validateConfirmation2faOTP.invalid === false && this.validateConfirmation2faOTP.minLength === false) {
          this.login();
      }
    },

    handle2fa() {
      this.confirmation2faOTP = '';

      this.validateConfirmation2faOTP.required = false;
      this.validateConfirmation2faOTP.invalid = false;
      this.validateConfirmation2faOTP.minLength = false;
      this.validateConfirmation2faOTP.dirty = false;

      this.twoFactorAuthRequired = true;
      this.init2faInputSyncHandler(this.twoFactorAuthRequired);

      setTimeout( () => {
        jQuery('#one-time-password-id').parent().addClass('md-focused');
        jQuery('#one-time-password-id').focus();
      }, 100);
    },

    get2FAValidationClass() {
      return {
        'md-invalid': this.validateConfirmation2faOTP.invalid && this.validateConfirmation2faOTP.dirty
      }
    },

    login() {
      this.sending = true;
      LoginService.login(this.form.email, this.form.password, this.confirmation2faOTP, this.twoFactorRecoveryCode, this.requestRecoveryCode)
        .then(response => {
          if (response.data.two_factor_auth_required) {
            this.sending = false;
            this.OTPAuthenticationFailed = false;
            this.handle2fa();
          } else {
            this.$store.commit('session', response.data);
            if (!this.requestRecoveryCode) {
              this.getLabels();
            }
            this.sending = false;
          }
        })
        .catch(e => {
          this.sending = false;
          this.confirmation2faOTP = '';
          this.OTPAuthenticationFailed = true;
          HttpErrorHandler.handleError(e, this, 'Fehler beim Anmelden des Benutzers mit Email ' + this.form.email);
          if (this.isAuthenticated) {
            this.logout();
          }
        });
    },

    onRequestRecoveryCode() {
      this.requestRecoveryCode = true;
      this.confirmation2faOTP = '';
      this.login();
    },

    cleanLocalStorage() {
      Object.entries(localStorage).forEach(entry => {
        if (entry[1].includes('undefined')) {
          localStorage.removeItem(entry[0]);
        }
      });
    },

    getLabels() {
      InfoService.getLabels()
        .then(response => {
          this.$store.commit('labelsInfo', response.data);
          this.getVersion();
        })
        .catch(e => {
          HttpErrorHandler.handleError(e, this, 'Fehler beim Laden der Labels');
        });
    },

    getVersion() {
      InfoService.getVersion()
        .then(response => {
          this.$store.commit('versionInfo', response.data);
          this.getUserInfo();
        })
        .catch(e => {
          HttpErrorHandler.handleError(e, this, 'Fehler beim Laden der Versionsdaten');
        });
    },

    getUserInfo() {
      UserService.getUserInfo()
        .then(response => {
          this.$store.commit('userInfo', response.data);

          if (this.isSage) {
            this.getPreFilledContract(false);
          }

          if (this.isParent) {
            this.getParentOrganizationInfo();
          }

          if (!this.isSage && !this.isParent) {
            this.getOrganizationInfo();
            this.getOrganizationContract();
            this.getPreFilledContract(true);
          } else {
            this.doRouting();
          }
        })
        .catch(e => {
          HttpErrorHandler.handleError(e, this, 'Fehler beim Laden der Informationen für Benutzer mit Email ' + this.form.email);
          this.sending = false;
          this.logout();
        });
    },

    getOrganizationInfo() {
      OrganizationService.getOrganizationInfo()
        .then(response => {
          this.$store.commit('organizationInfo', response.data);
          this.$store.commit('availableFacilities');
          this.sending = false;
          this.doRouting();
        })
        .catch(e => {
          HttpErrorHandler.handleError(e, this, 'Fehler beim Laden der Trägerdaten');
          this.$store.commit('organizationInfo', {name: '<?>'});
          this.sending = false;
        });
    },

    getOrganizationContract() {
      this.sending = true;
      OrganizationService.getOrganizationContract()
          .then(response => {
            this.$store.commit('organizationContract', response.data);
            this.sending = false;
          })
          .catch(e => {
            HttpErrorHandler.handleError(e, this, 'Fehler beim Laden der Vertragsdaten für Träger <b>' + this.organization.name + '</b>');
            this.sending = false;
          });
    },

    getParentOrganizationInfo() {
      this.sending = true;
      OrganizationInfoService.getOrganizationInfo(false, this.isParent)
        .then(response => {
          this.sending = false;

          let facilities = [];
          const facilityNames = response.data.facilityNames;
          const facilityTypes = response.data.facilityTypes;

          this.user.allowedFacilities.forEach(refNr => {
            facilities.push({
              referenceNumber: refNr,
              name: facilityNames[refNr],
              facilityType: facilityTypes[refNr]
            });
          });

          this.$store.commit('organizationInfo', {facilities: facilities});
          this.$store.commit('availableFacilities');
        })
        .catch(e => {
          HttpErrorHandler.handleError(e, this, 'Fehler beim Laden der Trägerdaten');
          this.$store.commit('organizationInfo', {name: '<?>'});
          this.sending = false;
        });
    },

    getPreFilledContract(org) {
      setTimeout(() => {
        this.demand.modules = this.labels.moduleTypes.reverse();
        InfoService.getPreFilledContract(this.demand, org)
          .then(response => {
            this.$store.commit('modules', response.data.ai42Contract.modules);
          })
          .catch(e => {
            HttpErrorHandler.handleError(e, this, 'Fehler beim Laden der Modul-Informationen');
          });
      }, 1000);
    },

    isAccessibleRoute(route) {
      if (route.beta) {
        if (!this.isBeta) {
          return false;
        }
      }

      const user = this.user;
      for (let userRole of user.roles) {
        for (let routeRole of route.roles) {
          if (routeRole.startsWith('!')) {
            if (userRole === routeRole.substring(1)) {
              return false;
            }
          } else {
            if (userRole === routeRole) {
              return true;
            }
          }
        }
      }
      return false;
    },

    processChallengeLogin() {
      // Process challenge login
      if (this.challengeLoginRoute.length > 0) {
        let challengeLoginRoute = this.challengeLoginRoute;
        this.$store.commit('challengeLoginRoute', '');
        if (HttpUrlService.isValidRoute(challengeLoginRoute)) {
          if (HttpUrlService.isAccessibleRoute(challengeLoginRoute)) {
            this.$router.push(challengeLoginRoute);
            return true;
          }
        }
      }

      return false;
    },

    doRouting() {
      if (this.processChallengeLogin()) {
        return;
      }

      if (localStorage.getItem(this.user.md5 + '@lastRouterPath')) {
        let lastRoute = localStorage.getItem(this.user.md5 + '@lastRouterPath');
        let routingAllowed = true;
        this.routingBlackList.forEach(route => {
          if (lastRoute.toLowerCase().includes(route.toLowerCase())) {
            console.log('- ' + lastRoute.toLowerCase())
            console.log('= ' + route.toLowerCase())
            routingAllowed = false;
            console.log('Skipping forbidden last route: ' + lastRoute);
          }
        });
        if (routingAllowed) {
          this.$router.push(lastRoute);
          return;
        }
      }

      if (!this.isSage) {
        if (this.isFacility) {
          this.$router.push('/');
          return;
        }
        if (this.isTreasurer) {
          this.$router.push('/Finanzen/Abrechnungen');
          return;
        }
        if (this.isHrmanager) {
          this.$router.push('/Verwaltung/Mitarbeiter');
          return;
        }
        if (this.isParent) {
          this.$router.push('/Aktuelles');
          return;
        }
        if (this.isDocumentation) {
          this.$router.push('/Kinder');
          return;
        }

        this.$router.push('/');

      } else {
        this.$router.push('/Organisationen');
      }
    },

    logout() {
      HttpInterceptor.forceLogout();
      this.$store.commit('logout');
      this.$router.push('/Logout');
    },
  },

  validations: {
    form: {
      email: {
        required,
        email
      },
      password: {
        required,
        minLength: minLength(5)
      }
    }
  },

  computed: {
    webAppConfig() {
      return this.$store.getters.webAppConfig;
    },
    isAuthenticated() {
      return this.$store.getters.authenticated;
    },
    user() {
      return this.$store.getters.user;
    },
    version() {
      return this.$store.getters.version;
    },
    labels() {
      return this.$store.getters.labels;
    },
    isBeta() {
      return this.$store.getters.beta;
    },
    isSage() {
      return this.$store.getters.sage;
    },
    isAdmin() {
      return this.$store.getters.admin;
    },
    isTreasurer() {
      return this.$store.getters.treasurer;
    },
    isHrmanager() {
      return this.$store.getters.hrmanager;
    },
    isFacility() {
      return this.$store.getters.facility;
    },
    isParent() {
      return this.$store.getters.parent;
    },
    isDocumentation() {
      return this.$store.getters.documentation;
    },
    routingHistory() {
      return this.$store.getters.routingHistory;
    },
    routingBlackList() {
      return this.$store.getters.routingBlackList;
    },
    challengeLoginRoute() {
      return this.$store.getters.challengeLoginRoute;
    },
    navigationRoutes() {
      return this.$store.getters.navigationRoutes;
    },
  }
}
</script>

<style lang="scss" scoped>
input, input:focus {
  border-width: 0;
  box-shadow: none;
}

.md-tooltip {
  font-size: medium;
  height: auto;
  white-space: pre-wrap;
}

</style>
